import { UserManager, WebStorageStateStore } from 'oidc-client';
import RouterHistory from '../Router/RouterHistory';
import { ApplicationPaths, ApplicationName } from './ApiAuthorizationConstants';
import { IDENTITY_CONFIG } from "./AdConfig";

class AuthorizeService {
  constructor() {
    this.callback = [];
    this.callbackOnce = [];
    this.userStateInitialize = false;
    this.beforeExpireRefreshTokenTime = 60 * 3;

    this.initializeUserManager();
    this.initializeUserState();
  }

  checkInitializationError() {
    if (!this.userStateInitialize) {
      console.error('User authorize state use before initialization');
    }
  }

  waitInitialization() {
    return this.initialize;
  }

  initializeUserState() {
    this.user = null;
    this.initialize = this.userManager.getUser()
      .then((user) => {
        this.updateUserState(user);
      })
      .catch((error) => {
        this.updateUserState(null);
        console.error('Initialization of user authorize state failed: ', error);
      });
  }

  initializeUserManager() {
    if (this.userManager !== undefined) {
      return;
    }

    let settings = IDENTITY_CONFIG;
    settings.automaticSilentRenew = true;
    settings.includeIdTokenInSilentRenew = true;
    settings.validateSubOnSilentRenew = true;
    settings.userStore = new WebStorageStateStore({
      prefix: ApplicationName
    });
    this.userManager = new UserManager(settings);
    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager.removeUser();
      this.updateUserState(null);
    });

    settings.accessTokenExpiringNotificationTime = this.beforeExpireRefreshTokenTime;
    this.userManager.events.addAccessTokenExpired(() => {
      console.log('AccessTokenExpired: renew');
      this.signInSilent();
    });

    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('AccessTokenExpiring: renew');
      this.signInSilent();
    });
  }

  tokenExpired() {
    if (!this.user || !this.user.expires_at) {
      return true;
    }
    const time = this.user.expires_at - (new Date().getTime() / 1000);
    if (time < this.beforeExpireRefreshTokenTime) {
      return true;
    }
    return false;
  }

  isAuthenticated() {
    this.checkInitializationError();
    return (this.user && this.user.profile && !this.tokenExpired());
  }

  getUserSession() {
    return this.user;
  }

  getUser() {
    this.checkInitializationError();

    return this.user && this.user.profile;
  }

  getAccessToken() {
    this.checkInitializationError();

    return this.user && this.user.access_token;
  }

  async signInSilent() {
    this.checkInitializationError();

    try {
      await this.userManager.clearStaleState();
      const user = await this.userManager.signinSilent({ useReplaceToNavigate: true });
      if (!user) {
        console.log('Silent renew login failure');
        return false;
      }
      console.log('Authenticated with silent renew');
      this.updateUserState(user);
      return true;
    } catch(error) {
      this.notifySubscribers();
      //console.warn('Silent authentication error: ', error);
    }

    return false;
  }

  getRedirectStoreKey() {
    return `${ApplicationName}_authorize_redirect`;
  }

  saveRedirectPath(redirectPath) {
    if (!redirectPath || redirectPath.substr(0, 1) != '/') {
      redirectPath = RouterHistory.location.pathname;
      // use window.location.pathname with hashtag+search if error
    }
    localStorage.setItem(this.getRedirectStoreKey(), redirectPath);
  }

  applyRedirectPath() {
    const redirectPath = localStorage.getItem(this.getRedirectStoreKey());
    localStorage.removeItem(this.getRedirectStoreKey());
    RouterHistory.push(redirectPath);
    //console.log('Redirect path:' + redirectPath);
    //window.location = window.location.protocol + '//' + window.location.host + redirectPath;
  }

  signInRedirect(redirectPath) {
    this.checkInitializationError();

    try {
      this.saveRedirectPath(redirectPath);
      this.userManager.signinRedirect(
        { useReplaceToNavigate: true, data: { returnUrl: window.location.origin + '/' + ApplicationPaths.LoginCallback } }
      );
    } catch (error) {
      console.error('Redirect authentication error: ', error);
    }
  }

  async signIn(redirectPath = null) {
    await this.initialize;
    if (this.isAuthenticated()) {
      return this.initialize;
    }
    const silent = this.signInSilent();
    const signIn = await silent;
    if (!signIn) {
      this.signInRedirect(redirectPath);
    }
    return silent;
  }

  async completeRedirectSignIn() {
    this.checkInitializationError();
    let user;

    try {
      user = await this.userManager.signinCallback(window.location.href);
      if (user) {
        this.updateUserState(user);
      }
    } catch(error) {
      this.notifySubscribers();
      console.error('Redirect authentication callback error: ', error);
      return error;
    }
    this.applyRedirectPath();

    return user;
  }

  signOut(redirectPath = null) {
    this.checkInitializationError();

    try {
      this.saveRedirectPath(redirectPath);
      this.userManager.signoutRedirect(
        { useReplaceToNavigate: true, data: { returnUrl: window.location.origin + '/' + ApplicationPaths.LogOutCallback } }
      );
    } catch (error) {
      console.error("Signout error: ", error);
    }
  }

  async completeSignOut() {
    this.checkInitializationError();

    try {
      await this.userManager.signoutCallback(window.location.href);
      this.applyRedirectPath();
      console.log('Logout done');
    } catch (error) {
      console.error('Redirect signout callback error: ', error);
    }
    this.updateUserState(null);
  }

  updateUserState(user) {
    this.user = user;
    this.userStateInitialize = true;
    this.notifySubscribers();
  }

  subscribe(listener) {
    if (!listener) {
      console.error('AuthorizeService: invalid listener!');
    }
    if (this.callback.indexOf(listener) != -1) {
      console.error('AuthorizeService: subscription duplicate found!');
    } else {
      if (this.userStateInitialize) {
        listener();
      }
      this.callback.push(listener);
    }
  }

  subscribeOnce(listener) {
    if (!listener) {
      console.error('AuthorizeService: invalid listener!');
    }
    if (this.callbackOnce.indexOf(listener) != -1) {
      console.error('AuthorizeService: subscription duplicate found!');
    } else {
      if (this.userStateInitialize) {
        listener();
      } else {
        this.callbackOnce.push(listener);
      }
    }
  }

  unsubscribe(listener) {
    if (!listener) {
      console.error('AuthorizeService: invalid listener!');
    }

    let listenerIdx = this.callback.indexOf(listener);
    if (listenerIdx < 0) {
      console.error('AuthorizeService: unsubscribe listener not found!');
    } else {
      this.callback.splice(listenerIdx, 1);
    }
  }

  notifySubscribers() {
    const currentOnceListeners = [ ...this.callbackOnce ];
    currentOnceListeners.forEach((listener) => {
      let rmListnerIdx = currentOnceListeners.indexOf(listener);
      if (rmListnerIdx != -1) {
        this.callbackOnce.splice(rmListnerIdx, 1);
      }
      listener();
    });
    this.callback.forEach((listener) => listener());
  }
}

const AuthenticationResultStatus = {
  Redirect: 'redirect',
  Success: 'success',
  Fail: 'fail'
};

export default new AuthorizeService();

export {
  AuthorizeService,
  AuthenticationResultStatus
};
